// src/pages/ChangePayment.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarAds from './Sidebars/SidebarAds';
import { useTranslation } from 'react-i18next';

const BG = '#050912';
const CARD_BG = '#0D101B';
const BORDER = '#22283a';
const GOLD = '#D4AF37';

type PaymentMethod = 'card' | 'paypal' | 'sepa' | 'wallet';

const ChangePayment: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [method, setMethod] = useState<PaymentMethod>('card');

  // Card fields
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  // PayPal
  const [paypalEmail, setPaypalEmail] = useState('');

  // SEPA
  const [sepaName, setSepaName] = useState('');
  const [sepaIban, setSepaIban] = useState('');

  // Wallet
  const [walletProvider, setWalletProvider] = useState<'apple' | 'google'>('apple');

  const handleSave = () => {
    // 🔧 later: send to backend
    // For now, just a simple confirm + navigate back
    alert(
      t(
        'ad.payment.saved',
        'Your payment method has been updated (demo only, no real billing yet).'
      )
    );
    navigate('/managebillings');
  };

  const handleCancel = () => {
    navigate('/advertisementstart');
  };

  const renderMethodForm = () => {
    switch (method) {
      case 'card':
        return (
          <div style={formGrid}>
            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.card.name', 'Card holder name')}
              </label>
              <input
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder={t(
                  'ad.payment.card.namePlaceholder',
                  'Name as displayed on the card'
                )}
                style={inputStyle}
              />
            </div>

            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.card.number', 'Card number')}
              </label>
              <input
                type="text"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="•••• •••• •••• ••••"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...formField, flex: 1 }}>
                <label style={labelStyle}>
                  {t('ad.payment.card.expiry', 'Expiry')}
                </label>
                <input
                  type="text"
                  value={cardExpiry}
                  onChange={(e) => setCardExpiry(e.target.value)}
                  placeholder="MM/YY"
                  style={inputStyle}
                />
              </div>
              <div style={{ ...formField, flex: 1 }}>
                <label style={labelStyle}>
                  {t('ad.payment.card.cvc', 'CVC')}
                </label>
                <input
                  type="password"
                  value={cardCvc}
                  onChange={(e) => setCardCvc(e.target.value)}
                  placeholder="•••"
                  style={inputStyle}
                />
              </div>
            </div>

            <p style={hintStyle}>
              {t(
                'ad.payment.card.hint',
                'We support Visa, Mastercard and American Express. Your card details are processed securely.'
              )}
            </p>
          </div>
        );

      case 'paypal':
        return (
          <div style={formGrid}>
            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.paypal.email', 'PayPal email')}
              </label>
              <input
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
              />
            </div>
            <p style={hintStyle}>
              {t(
                'ad.payment.paypal.hint',
                'You will be redirected to PayPal for confirming future charges when needed.'
              )}
            </p>
          </div>
        );

      case 'sepa':
        return (
          <div style={formGrid}>
            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.sepa.name', 'Account holder')}
              </label>
              <input
                type="text"
                value={sepaName}
                onChange={(e) => setSepaName(e.target.value)}
                placeholder={t(
                  'ad.payment.sepa.namePlaceholder',
                  'Full name of the account owner'
                )}
                style={inputStyle}
              />
            </div>
            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.sepa.iban', 'IBAN')}
              </label>
              <input
                type="text"
                value={sepaIban}
                onChange={(e) => setSepaIban(e.target.value)}
                placeholder="DE89 3704 0044 0532 0130 00"
                style={inputStyle}
              />
            </div>
            <p style={hintStyle}>
              {t(
                'ad.payment.sepa.hint',
                'By saving a SEPA bank account you authorise recurring debits for your advertising charges.'
              )}
            </p>
          </div>
        );

      case 'wallet':
        return (
          <div style={formGrid}>
            <div style={formField}>
              <label style={labelStyle}>
                {t('ad.payment.wallet.provider', 'Wallet provider')}
              </label>
              <select
                value={walletProvider}
                onChange={(e) =>
                  setWalletProvider(e.target.value === 'google' ? 'google' : 'apple')
                }
                style={inputStyle}
              >
                <option value="apple">
                  {t('ad.payment.wallet.apple', 'Apple&nbsp;Pay')}
                </option>
                <option value="google">
                  {t('ad.payment.wallet.google', 'Google&nbsp;Pay')}
                </option>
              </select>
            </div>
            <p style={hintStyle}>
              {t(
                'ad.payment.wallet.hint',
                'You will complete the connection with your wallet provider on your next payment confirmation.'
              )}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: BG,
        color: '#eee',
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Sidebar */}
      <SidebarAds />

      {/* Main content */}
      <main
        style={{
          marginLeft: 200,
          padding: '24px 32px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              💳 {t('ad.payment.title', 'Change payment method')}
            </h1>
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                color: '#9ca3af',
                fontSize: 14,
                maxWidth: 520,
              }}
            >
              {t(
                'ad.payment.subtitle',
                'Select how you want to pay for your advertising campaigns. You can update this at any time.'
              )}
            </p>
          </div>

          
        </header>

        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1.2fr)',
            gap: 20,
          }}
        >
          {/* Left: method selection + form */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              {t('ad.payment.methods.title', 'Payment methods')}
            </h2>

            {/* Method selector */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 10,
                marginBottom: 16,
              }}
            >
              <MethodTile
                label={t('ad.payment.methods.card', 'Credit / Debit card')}
                icon="💳"
                active={method === 'card'}
                onClick={() => setMethod('card')}
              />
              <MethodTile
                label="PayPal"
                icon="🅿️"
                active={method === 'paypal'}
                onClick={() => setMethod('paypal')}
              />
              <MethodTile
                label={t('ad.payment.methods.sepa', 'SEPA bank debit')}
                icon="🏦"
                active={method === 'sepa'}
                onClick={() => setMethod('sepa')}
              />
              <MethodTile
                label={t('ad.payment.methods.wallet', 'Apple / Google Pay')}
                icon="📱"
                active={method === 'wallet'}
                onClick={() => setMethod('wallet')}
              />
            </div>

            <div
              style={{
                marginTop: 4,
                marginBottom: 10,
                fontSize: 12,
                color: '#9ca3af',
              }}
            >
              {t(
                'ad.payment.methods.note',
                'Your default method will be used for all future charges unless a campaign requires additional confirmation.'
              )}
            </div>

            {/* Dynamic form */}
            <div
              style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              {renderMethodForm()}
            </div>

            {/* Actions */}
            <div
              style={{
                marginTop: 20,
                display: 'flex',
                gap: 10,
              }}
            >
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 18px',
                  borderRadius: 999,
                  border: `1px solid ${GOLD}`,
                  background:
                    'radial-gradient(circle at top left, #fceabb 0%, #f8b500 45%, #b88700 100%)',
                  color: '#111',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  boxShadow: '0 0 18px rgba(212, 175, 55, 0.35)',
                }}
              >
                {t('ad.payment.buttons.save', 'Save changes')}
              </button>

              <button
                onClick={handleCancel}
                style={{
                  padding: '10px 16px',
                  borderRadius: 999,
                  border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: '#e5e7eb',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t('ad.payment.buttons.cancel', 'Cancel')}
              </button>
            </div>
          </div>

          {/* Right: info / security / current method */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 18px 35px rgba(0,0,0,0.3)',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {t('ad.payment.security.title', 'Security & billing info')}
            </h2>

            <ul
              style={{
                margin: '6px 0 14px',
                paddingLeft: 18,
                fontSize: 13,
                color: '#d1d5db',
                lineHeight: 1.5,
              }}
            >
              <li>
                {t(
                  'ad.payment.security.point1',
                  'Card and bank details are sent via encrypted connections and are never stored directly on our servers.'
                )}
              </li>
              <li>
                {t(
                  'ad.payment.security.point2',
                  'Charges are usually bundled once per day for active campaigns, based on impressions and clicks.'
                )}
              </li>
              <li>
                {t(
                  'ad.payment.security.point3',
                  'You can pause campaigns at any time from the Ads overview to stop future charges.'
                )}
              </li>
            </ul>

            <div
              style={{
                marginTop: 10,
                padding: 12,
                borderRadius: 12,
                border: `1px solid rgba(148,163,184,0.5)`,
                background:
                  'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.98))',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: '#9ca3af',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                }}
              >
                {t('ad.payment.currentMethod.label', 'Current method (example)')}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: '#e5e7eb',
                  marginBottom: 4,
                }}
              >
                💳 Visa •••• 1234
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                {t(
                  'ad.payment.currentMethod.hint',
                  'New charges will use the method you save here. This block only shows a demo card for now.'
                )}
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                fontSize: 11,
                color: '#6b7280',
              }}
            >
              {t(
                'ad.payment.smallPrint',
                'Important: This is a preview UI. In production, please ensure you comply with local regulations (PSD2, SCA, card scheme rules, etc.) and use a PCI-compliant payment provider.'
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

type MethodTileProps = {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
};

const MethodTile: React.FC<MethodTileProps> = ({ label, icon, active, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 14,
        border: active ? `1px solid ${GOLD}` : `1px solid ${BORDER}`,
        background: active
          ? 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(15,23,42,0.95))'
          : 'rgba(15,23,42,0.95)',
        color: '#e5e7eb',
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 13,
        textAlign: 'left' as const,
        boxShadow: active ? '0 0 14px rgba(212,175,55,0.45)' : 'none',
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
};

const formGrid: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const formField: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#d1d5db',
};

const inputStyle: React.CSSProperties = {
  backgroundColor: '#020617',
  borderRadius: 10,
  border: '1px solid rgba(148,163,184,0.8)',
  padding: '8px 10px',
  color: '#e5e7eb',
  fontSize: 13,
  outline: 'none',
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  marginTop: 6,
};

export default ChangePayment;
